import _merge from "lodash/merge";
import _debounce from "lodash/debounce";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Highcharts } from 'core/components';
import styled, { useTheme } from 'styled-components';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import $syn from 'app/stores/synthetics/$syn';
import { generateHighchartsOptions } from './util/highcharts';
const ChartContainer = styled.div.withConfig({
  displayName: "HighchartsDataView__ChartContainer",
  componentId: "sc-uq3vc3-0"
})(["&&{flex:1;min-height:0;width:100%;position:relative;}"]);
/** Dimensions that should render agent names instead of IDs */
const AGENT_DIMENSIONS = ['src_agent', 'dst_agent'];

/** Dimensions that should render test names instead of IDs */
const TEST_DIMENSIONS = ['test_id'];
function HighchartsDataView({
  queryModel,
  measurement,
  onChartRef,
  chartOverrides
}) {
  const theme = useTheme();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(400);

  // Create a dimension formatter that looks up agent/test names from the $syn store
  const dimensionFormatter = useCallback((dimensionName, value) => {
    if (AGENT_DIMENSIONS.includes(dimensionName) && value) {
      const {
        agents
      } = $syn;
      const agent = agents?.get(value);
      return agent?.label || value;
    }
    if (TEST_DIMENSIONS.includes(dimensionName) && value) {
      const {
        tests
      } = $syn;
      const test = tests?.get(value);
      return test?.get('display_name') || test?.get('name') || value;
    }
    return value;
  }, []);
  const {
    chartOptions,
    seriesRowIds
  } = useMemo(() => {
    const result = generateHighchartsOptions(queryModel, measurement, theme, dimensionFormatter);
    const height = result.options.chart?.height ?? containerHeight ?? undefined;
    const baseOptions = {
      ...result.options,
      chart: {
        ...result.options.chart,
        height
      }
    };
    return {
      chartOptions: chartOverrides ? _merge({}, baseOptions, chartOverrides) : baseOptions,
      seriesRowIds: result.seriesRowIds
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryModel, queryModel.effectiveVizLimit, queryModel.results, queryModel.results.timestamps, queryModel.settings, theme, measurement, containerHeight, dimensionFormatter, chartOverrides]);

  // Publish charted row IDs to QueryModel so the legend knows which rows have chart series.
  // useLayoutEffect runs synchronously before the browser paints, preventing the 1-frame flash
  // where all rows appear charted (which happened with useEffect's deferred timing).
  useLayoutEffect(() => {
    queryModel.setChartedRowIds(seriesRowIds);
  }, [seriesRowIds, queryModel]);

  // Sync series visibility: observe seriesVisibility map → call series.setVisible()
  // Uses queryModel.chartedRowIds (@observable.ref) in the tracking function so MobX
  // properly re-tracks when the chart rebuilds with new row IDs after query transitions.
  useEffect(() => {
    const disposer = reaction(() => queryModel.chartedRowIds.map(id => queryModel.isSeriesVisible(id)), visibilityList => {
      const chart = chartRef.current;
      if (!chart) {
        return;
      }

      // For aggregate charts (single series, multiple points): toggle point visibility
      const isAggregate = chart.series.length === 1 && visibilityList.length > 1;
      if (isAggregate) {
        const series = chart.series[0];
        visibilityList.forEach((visible, idx) => {
          const point = series?.data?.[idx];
          if (point && point.visible !== visible) {
            // Highcharts types don't include `visible` in PointOptionsObject but it works at runtime for pie/bar
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            point.update({
              visible
            }, false);
          }
        });
      } else {
        visibilityList.forEach((visible, idx) => {
          const series = chart.series[idx];
          if (series && series.visible !== visible) {
            series.setVisible(visible, false);
          }
        });
      }
      chart.redraw();
    }, {
      fireImmediately: true
    });
    return disposer;
  }, [queryModel]);

  // Sync hovered series: observe hoveredSeriesId → call series.setState()
  // Tracks both hoveredSeriesId and chartedRowIds so the reaction re-runs with
  // fresh row→index mapping after query transitions.
  useEffect(() => {
    let prevHoveredId = null;
    const disposer = reaction(() => ({
      hoveredId: queryModel.hoveredSeriesId,
      rowIds: queryModel.chartedRowIds
    }), ({
      hoveredId,
      rowIds
    }) => {
      const chart = chartRef.current;
      if (!chart) {
        return;
      }

      // For aggregate charts (single series, multiple points): highlight points instead of series
      const isAggregate = chart.series.length === 1 && rowIds.length > 1;

      // Unhighlight previous
      if (prevHoveredId) {
        const prevIdx = rowIds.indexOf(prevHoveredId);
        if (prevIdx >= 0) {
          if (isAggregate) {
            const point = chart.series[0]?.data?.[prevIdx];
            if (point) {
              point.setState();
            }
          } else if (chart.series[prevIdx]) {
            chart.series[prevIdx].setState();
          }
        }
        chart.renderer.boxWrapper.removeClass('highcharts-legend-series-active');
      }

      // Highlight current
      if (hoveredId) {
        const idx = rowIds.indexOf(hoveredId);
        if (idx >= 0) {
          if (isAggregate) {
            const point = chart.series[0]?.data?.[idx];
            if (point) {
              point.setState('hover');
            }
          } else if (chart.series[idx]) {
            chart.series[idx].setState('hover');
          }
          chart.renderer.boxWrapper.addClass('highcharts-legend-series-active');
        }
      }
      prevHoveredId = hoveredId;
    }, {
      fireImmediately: false
    });
    return disposer;
  }, [queryModel]);
  const chartCallback = useCallback(chart => {
    // If there is an existing chart instance, notify consumer that it is being replaced
    if (chartRef.current && chartRef.current !== chart) {
      onChartRef?.(null);
    }
    chartRef.current = chart;
    onChartRef?.(chart);
  }, [onChartRef]);

  // Notify consumer when the chart is destroyed on unmount
  useEffect(() => () => {
    onChartRef?.(null);
  }, [onChartRef]);

  // Use ResizeObserver to track container size and update chart height
  useEffect(() => {
    const container = containerRef.current;
    // we can't resize without a container, and we shouldn't resize charts that have a fixed size
    // note that we can't rely on `chartOptions.chart.height` to tell us that though, due to how `containerHeight` works.
    if (!container || queryModel.settings.chart.type === 'timelineView') {
      return undefined;
    }
    const handleResize = _debounce(entries => {
      const entry = entries[0];
      if (entry) {
        const newHeight = entry.contentRect.height;
        setContainerHeight(newHeight);
        // Also resize the chart if it exists and is fully initialized
        if (chartRef.current?.options) {
          chartRef.current.setSize(undefined, newHeight, false);
        }
      }
    }, 100);
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    return () => {
      handleResize.cancel();
      resizeObserver.disconnect();
    };
  }, [queryModel.settings.chart.type]);

  // the key forces the chart to reinitialize when the chart type changes
  return /*#__PURE__*/React.createElement(ChartContainer, {
    ref: containerRef
  }, (chartOptions.chart?.height || containerHeight !== null) && /*#__PURE__*/React.createElement(Highcharts, {
    key: queryModel.settings.chart.type,
    styledMode: false,
    callback: chartCallback,
    options: chartOptions,
    allowChartUpdate: true
  }));
}
export default observer(HighchartsDataView);